/**
 * @fileoverview Shared, host-independent data-bound widget resolution and write-back.
 * @module core-domain/data-bound
 *
 * Responsibilities:
 * - Define structural metadata for data-bound widgets.
 * - Resolve and format values across local data sources without host imports.
 * - Provide immutable writes, lookup catalog construction, and effective responses.
 *
 * Dependencies:
 * - `../dateUtils` for timezone-safe calendar date formatting.
 *
 * Architecture:
 * - Pure domain functions; host applications provide source snapshots and persistence.
 *
 * Notes:
 * - This module must remain independent of ui-admin and host-specific databases.
 *
 * @contract DataBoundWidgetMeta
 * Purpose: Structural data-bound widget metadata shared across hosts.
 * Fields: widgetId, dataSource, boundField?, compositeFields?, displayFormat?,
 *   inputType?, defaultMode?, lookupType?.
 * Rules: Structural only; additional host metadata may be present.
 * Sync: Local-first consumers own persistence and synchronization.
 *
 * @contract DataBoundResolveContext
 * Purpose: Source snapshots and service-area selector for one resolution.
 * Fields: sources, serviceAreaTypeCode?.
 * Rules: Missing sources resolve as not found.
 * Sync: Read-only snapshots; writes are returned as new objects.
 */
import { formatCalendarDate } from '../dateUtils';
import type { FieldResponse, InspectionForm } from '../inspection/types';

export interface DataBoundWidgetMeta {
  widgetId: string;
  dataSource: string;
  boundField?: string;
  compositeFields?: string[];
  displayFormat?: string;
  inputType?: string;
  defaultMode?: string;
  lookupType?: string;
}

/**
 * Merge registered widget metadata with field-level overrides.
 *
 * @param registered Registry metadata, when a widget id is registered.
 * @param overrides Explicit field metadata, which takes precedence.
 * @returns Complete structural metadata for resolution and writes.
 */
export function mergeDataBoundWidgetMeta(
  registered: DataBoundWidgetMeta | undefined,
  overrides: Partial<DataBoundWidgetMeta> = {},
): DataBoundWidgetMeta {
  const definedOverrides = Object.fromEntries(
    Object.entries(overrides).filter(([, value]) => value !== undefined),
  ) as Partial<DataBoundWidgetMeta>;
  return {
    ...registered,
    ...definedOverrides,
    widgetId: definedOverrides.widgetId ?? registered?.widgetId ?? '',
    dataSource: definedOverrides.dataSource ?? registered?.dataSource ?? '',
  };
}

/**
 * @contract ResolvedDataBoundValue
 * Purpose: Carries a resolved raw value and human-readable display.
 * Fields: display, raw, found.
 * Rules: `found` is false when no bound value exists.
 * Sync: Read-only resolution result.
 */
export interface ResolvedDataBoundValue {
  display: string;
  raw: unknown;
  found: boolean;
}

export interface DataBoundResolveContext {
  sources: Partial<Record<string, Record<string, unknown>>>;
  serviceAreaTypeCode?: string;
}

/**
 * @contract DataBoundLookupValue
 * Purpose: Normalized local lookup record.
 * Fields: lookupId, lookupType, code, name.
 * Rules: Values are grouped by lookup type and retain their Dexie identity.
 * Sync: Lookup catalog input only.
 */
export interface DataBoundLookupValue {
  lookupId: string;
  lookupType: string;
  code: string;
  name: string;
}

/**
 * @contract DataBoundLookupOption
 * Purpose: Renderer option shape for one lookup value.
 * Fields: lookupId, lookupType, value, label.
 * Rules: Values remain strings at the renderer boundary and preserve their
 *   source identity for reference-valued writes.
 * Sync: Lookup catalog output only.
 */
export interface DataBoundLookupOption {
  lookupId: string;
  lookupType: string;
  value: string;
  label: string;
}

export type DataSourceType = 'inspectableEntity' | 'inspectionEvent' | 'inspectionSeries' | 'cases';
export type ResolvedValue = ResolvedDataBoundValue;
export type ResolveContext = DataBoundResolveContext;
export type DataBoundWidgetDefinition = DataBoundWidgetMeta;

/**
 * Select an event by its explicit event index, or by position for indexless events.
 *
 * @param events Event records from a case.
 * @param index Event index to select.
 * @returns The matching event, or `undefined` when there is no match.
 */
export function selectEventByIndex<T extends { eventIndex?: number | null }>(
  events: readonly T[] | null | undefined,
  index: number,
): T | undefined {
  if (!events) return undefined;
  const matching = events.find((event) => event.eventIndex === index);
  if (matching) return matching;
  const positional = events[index];
  return positional?.eventIndex == null ? positional : undefined;
}

interface DataBoundFieldRef {
  id: string;
  type?: string;
  widgetType?: string;
}

/**
 * Read a dot-separated path without mutating the source.
 *
 * @param obj Source object, if present.
 * @param path Dot-separated field path.
 * @returns The path value, or `undefined` when an intermediate segment is absent.
 */
export function getByPath(obj: Record<string, unknown> | undefined | null, path: string): unknown {
  if (!obj || !path) return undefined;
  let current: unknown = obj;
  for (const segment of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * Return a cloned source with a value set at a dot-separated path.
 *
 * @param obj Source object.
 * @param path Dot-separated field path.
 * @param value Value to write.
 * @returns A new root and cloned nested object chain.
 */
export function setByPath(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  if (!path) return { ...obj };
  const segments = path.split('.');
  const root = { ...obj };
  let current = root;

  for (const segment of segments.slice(0, -1)) {
    const next = current[segment];
    current[segment] = next != null && typeof next === 'object' && !Array.isArray(next)
      ? { ...(next as Record<string, unknown>) }
      : {};
    current = current[segment] as Record<string, unknown>;
  }

  current[segments[segments.length - 1]] = value;
  return root;
}

/**
 * Format a raw data-bound value for human-readable display.
 *
 * @param raw Raw resolved value.
 * @param displayFormat Optional builder display format.
 * @returns A safe display string; object values never stringify as `[object Object]`.
 */
export function formatDataBoundValue(raw: unknown, displayFormat?: string): string {
  if (raw == null) return '';

  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const lookup = raw as Record<string, unknown>;
    if (typeof lookup.name === 'string') return lookup.name;
    if (typeof lookup.code === 'string') return lookup.code;
  }

  switch (displayFormat) {
    case 'date': {
      if (typeof raw === 'string') {
        const formatted = formatCalendarDate(
          raw,
          'en-US',
          { month: '2-digit', day: '2-digit', year: 'numeric' },
          '',
        );
        if (formatted) return formatted;
      }
      return String(raw);
    }
    case 'currency': {
      const number = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
      return Number.isNaN(number)
        ? String(raw)
        : `$${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    case 'number': {
      const number = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
      return Number.isNaN(number) ? String(raw) : number.toLocaleString('en-US');
    }
    case 'phone':
    case 'email':
    case 'address':
    case 'lookup':
      return String(raw);
    case 'text':
    default:
      if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
      if (Array.isArray(raw)) return raw.join(', ');
      return String(raw);
  }
}

/**
 * Resolve a widget's raw and display values from the supplied source snapshots.
 *
 * @param widget Widget metadata.
 * @param context Source snapshots and optional service-area type code.
 * @returns Display and raw values plus whether a value was found.
 */
export function resolveDataBoundValue(
  widget: DataBoundWidgetMeta,
  context: DataBoundResolveContext,
): ResolvedDataBoundValue {
  const isServiceArea = widget.widgetId === 'entity.serviceArea' || widget.boundField === 'serviceAreas';
  if (isServiceArea && !context.serviceAreaTypeCode) {
    return { display: '(select service area type)', raw: undefined, found: false };
  }

  const sourceKey = widget.dataSource === 'inspectionCases' ? 'cases' : widget.dataSource;
  const source = context.sources[sourceKey];
  if (!source) return { display: '', raw: undefined, found: false };

  if (isServiceArea && context.serviceAreaTypeCode) {
    const serviceAreas = source.serviceAreas;
    if (!Array.isArray(serviceAreas)) return { display: '', raw: undefined, found: false };
    const match = serviceAreas.find((entry) => (
      entry != null
      && typeof entry === 'object'
      && (entry as Record<string, unknown>).typeCode === context.serviceAreaTypeCode
    )) as Record<string, unknown> | undefined;
    const raw = match?.value;
    return {
      display: formatDataBoundValue(raw, widget.displayFormat),
      raw,
      found: raw !== undefined && raw !== null,
    };
  }

  if (widget.compositeFields?.length) {
    const parts = widget.compositeFields
      .map((path) => getByPath(source, path))
      .filter((value) => value !== undefined && value !== null && value !== '')
      .map((value) => formatDataBoundValue(value));
    const joined = parts.join(' ');
    return { display: joined, raw: joined || undefined, found: parts.length > 0 };
  }

  if (!widget.boundField) return { display: '', raw: undefined, found: false };
  const raw = getByPath(source, widget.boundField);
  return {
    display: formatDataBoundValue(raw, widget.displayFormat),
    raw,
    found: raw !== undefined && raw !== null,
  };
}

/**
 * Resolve a form field using its extras and optional registry metadata.
 *
 * @param field Form field whose data-bound configuration is stored in extras.
 * @param context Source snapshots and optional service-area selector.
 * @param resolveWidgetDef Registry lookup for widget metadata.
 * @returns Display and raw values plus whether a value was found.
 */
export function resolveDataBoundFieldResponse(
  field: Pick<InspectionForm['fields'][number], 'extras'>,
  context: DataBoundResolveContext,
  resolveWidgetDef?: (widgetId: string) => DataBoundWidgetMeta | undefined,
): ResolvedDataBoundValue {
  const extras = field.extras ?? {};
  const widgetId = typeof extras.widgetId === 'string' ? extras.widgetId : undefined;
  const registered = widgetId ? resolveWidgetDef?.(widgetId) : undefined;
  const widget = mergeDataBoundWidgetMeta(registered, {
    widgetId,
    dataSource: typeof extras.dataSource === 'string' ? extras.dataSource : undefined,
    boundField: typeof extras.boundField === 'string' ? extras.boundField : undefined,
    compositeFields: Array.isArray(extras.compositeFields)
      ? extras.compositeFields.filter((part): part is string => typeof part === 'string')
      : undefined,
    displayFormat: typeof extras.displayFormat === 'string' ? extras.displayFormat : undefined,
    inputType: typeof extras.inputType === 'string' ? extras.inputType : undefined,
    defaultMode: typeof extras.defaultMode === 'string' ? extras.defaultMode : undefined,
    lookupType: typeof extras.lookupType === 'string' ? extras.lookupType : undefined,
  });
  const serviceAreaTypeCode = typeof extras.serviceAreaTypeCode === 'string'
    ? extras.serviceAreaTypeCode
    : context.serviceAreaTypeCode;

  return resolveDataBoundValue(widget, { ...context, serviceAreaTypeCode });
}

/**
 * Resolve a widget using the tenant resolver's established function name.
 *
 * @param widget Widget metadata.
 * @param context Source snapshots and optional service-area type code.
 * @returns Resolved display and raw values.
 */
export const resolveWidget = resolveDataBoundValue;

/**
 * Return a source updated at the widget's writable bound path.
 *
 * Composite and service-area widgets are read-only and return `null`.
 *
 * @param widget Widget metadata.
 * @param source Source object to update.
 * @param value New raw value.
 * @returns A new source object, or `null` when the widget is not writable.
 */
export function writeDataBoundValue(
  widget: DataBoundWidgetMeta,
  source: Record<string, unknown>,
  value: unknown,
): Record<string, unknown> | null {
  if (!widget.boundField || widget.compositeFields?.length || widget.widgetId === 'entity.serviceArea') {
    return null;
  }
  const updated = setByPath(source, widget.boundField, value);
  if (widget.dataSource === 'inspectableEntity' && widget.boundField === 'name') {
    return { ...updated, entityName: value };
  }
  return updated;
}

/**
 * Write a widget using the tenant resolver's established function name.
 *
 * @param widget Widget metadata.
 * @param source Source object to update.
 * @param value New raw value.
 * @returns An updated source or `null` when the widget is read-only.
 */
export const writeWidget = writeDataBoundValue;

/**
 * Resolve a list of widgets into a map keyed by widget id.
 *
 * @param widgets Widget metadata definitions.
 * @param context Source snapshots and optional service-area type code.
 * @returns Resolved values keyed by `widgetId`.
 */
export function resolveWidgets(
  widgets: readonly DataBoundWidgetMeta[],
  context: DataBoundResolveContext,
): Map<string, ResolvedDataBoundValue> {
  return new Map(widgets.map((widget) => [widget.widgetId, resolveDataBoundValue(widget, context)]));
}

/**
 * Build synchronous renderer lookup options from local lookup records.
 *
 * @param values Lookup records containing type, code, and display name.
 * @returns Options grouped by lookup type.
 */
export function buildLookupCatalog(
  values: readonly DataBoundLookupValue[],
): Map<string, DataBoundLookupOption[]> {
  const catalog = new Map<string, DataBoundLookupOption[]>();
  for (const item of values) {
    const options = catalog.get(item.lookupType) ?? [];
    options.push({
      lookupId: item.lookupId,
      lookupType: item.lookupType,
      value: item.code,
      label: item.name,
    });
    if (!catalog.has(item.lookupType)) catalog.set(item.lookupType, options);
  }
  return catalog;
}

/**
 * Hydrate only unanswered data-bound fields with their raw resolved value.
 *
 * @param forms Forms containing data-bound fields.
 * @param responsesByField Existing responses keyed by field id.
 * @param resolveField Resolver for one form field.
 * @returns A new response map with found values injected without replacing answers.
 */
export function buildEffectiveResponsesWithDataBound(
  forms: readonly Pick<InspectionForm, 'fields'>[],
  responsesByField: Record<string, FieldResponse>,
  resolveField: (field: InspectionForm['fields'][number]) => ResolvedDataBoundValue | null,
): Record<string, FieldResponse> {
  const responses = { ...responsesByField };
  for (const form of forms) {
    for (const field of form.fields as Array<InspectionForm['fields'][number] & DataBoundFieldRef>) {
      if (field.type !== 'dataBound' && field.widgetType !== 'dataBound') continue;
      const existing = responses[field.id];
      const value = existing?.value;
      if (value !== undefined && value !== null && value !== '') continue;
      const resolved = resolveField(field);
      if (resolved?.found) responses[field.id] = { value: resolved.raw, isNA: false };
    }
  }
  return responses;
}
